"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { BffFailure, clientGet } from "@/lib/http/client-http";
import { BffErrorCode } from "@/lib/http/bff-error-codes";
import { SESSION_EXPIRED_REDIRECT } from "./session-expired-redirect";
import { catalogQueryToSearchParams, type CatalogQuery } from "./catalog-query";
import type { CatalogPage, ListedPiece } from "./catalog-types";
import { EMPTY_CATALOG_PAGE } from "./panel-zero-state";

/**
 * One page of the catalog to explore, filtered and counted by the server (filters spec §9).
 *
 * The key is the route **and** the whole query, so every change of search, facet, order or page
 * is its own request and its own cache entry — going back to a page already seen costs nothing.
 * The first page is seeded from the server render, so the scope never opens on a skeleton.
 *
 * **The route is part of the key, and the seed only exists when there is one.** Both were
 * wrong, and together they emptied Minha Lista (reported on the VPS, 2026-10-08): the host came
 * through a suggestion's preview, which draws the same section without any server page, so it
 * seeded an **empty** page under the key the panel was about to use; the panel's own page was
 * then ignored, because React Query keeps the entry it already has, and `staleTime` held the
 * emptiness for a minute. The grid showed only the pieces already on the list, the counter said
 * "0 peças" and the facet rails drew nothing — until the entry went stale and the next mount
 * fetched for real, which is why switching tabs a few times "fixed" it.
 */

type CatalogResponse = ({ ok: true } & CatalogPage) | { ok: false; code: string };

export const HOST_CATALOG_ENDPOINT = "/api/host/catalog";

/**
 * Query key, exported so the cache and the tests share one definition.
 *
 * The route comes first because it is what the page **is**: the host's catalogue and the one a
 * consultant reads through the Staff_BFF are different collections, and two screens of the host
 * can hold different pages of the same query. It used to be the query alone, with a comment
 * claiming the page "is the catalog's alone, never joined with a list" — and that was not true
 * even then: the host's page is read with `joinList`, so it carries what is on *this* list.
 */
export function hostCatalogQueryKey(query: CatalogQuery, endpoint: string = HOST_CATALOG_ENDPOINT) {
  return ["host-catalog", endpoint, catalogQueryToSearchParams(query).toString()] as const;
}

async function fetchCatalogPage(query: CatalogQuery, endpoint: string): Promise<CatalogPage> {
  const search = catalogQueryToSearchParams(query).toString();
  const response = await clientGet<CatalogResponse>(
    search.length > 0 ? `${endpoint}?${search}` : endpoint,
  );

  if (!response.ok) {
    // An expired session is answered the way the server render answers it: the person is sent
    // back to log in, instead of reading a catalogue that is not empty as empty.
    if (response.code === BffErrorCode.UNAUTHENTICATED && typeof window !== "undefined") {
      window.location.href = SESSION_EXPIRED_REDIRECT;
    }

    // The BFF's error envelope is not an empty catalog: saying "nothing to add" when the read
    // failed would be a lie the grid cannot take back.
    throw new BffFailure(response.code);
  }

  const { ok: _ok, ...page } = response;

  return page;
}

export type UseHostCatalogOptions = {
  query: CatalogQuery;
  /**
   * The page the server already rendered, and the query it answers — **absent when the screen
   * has none**, which is not the same as an empty one.
   *
   * A screen with nothing to seed must seed nothing: `initialData` means "I already hold this
   * exact page", and handing over an empty page instead wrote emptiness into the cache as
   * though it were an answer. The suggestion's preview did that to Minha Lista.
   */
  initial?: { query: CatalogQuery; page: CatalogPage };
  /** False in the list scope: the catalog is not being read at all. */
  enabled: boolean;
  /**
   * The BFF route that serves the pages. Default: the host's, `HOST_CATALOG_ENDPOINT`. Another
   * flow (the internal list creation) reads the catalog through its own route — and the key
   * carries it, so the two never answer for each other.
   */
  endpoint?: string;
};

export function useHostCatalog({ query, initial, enabled, endpoint = HOST_CATALOG_ENDPOINT }: UseHostCatalogOptions) {
  const key = hostCatalogQueryKey(query, endpoint);
  const seeded =
    initial !== undefined && key[2] === hostCatalogQueryKey(initial.query, endpoint)[2]
      ? initial.page
      : undefined;

  const request = useQuery({
    queryKey: key,
    queryFn: () => fetchCatalogPage(query, endpoint),
    enabled,
    initialData: seeded,
    // A page already read does not change under the person while they browse back to it, so
    // going back costs no request and no wait. A slice the cache has never seen declares its
    // wait in skeleton instead of holding the previous one on screen (filters spec §8) — which
    // is why there is no `placeholderData` here.
    staleTime: 60_000,
  });

  return {
    // Never the seeded page as a stand-in for a failure: a failed slice keeps whatever is on
    // screen and says so through `isError`, instead of redrawing the unfiltered catalogue under
    // chips that claim a filter is on. With nothing seeded there is nothing to hold, and the
    // empty page is what "no page yet" looks like — `isLoading` is what the grid reads then.
    page: request.data ?? (request.isError ? EMPTY_CATALOG_PAGE : initial?.page ?? EMPTY_CATALOG_PAGE),
    /** A new slice is on the way and there is nothing of it to show yet. */
    isLoading: request.isPending || (request.isFetching && request.data === undefined),
    /** The grid holds the previous page while the next one arrives (§8's skeleton pulse). */
    isRefreshing: request.isFetching && request.data !== undefined,
    isError: request.isError,
    retry: request.refetch,
  };
}

/**
 * Every page up to the current one, for the narrow screens' "Carregar mais" (§7).
 *
 * The pages are already in the cache — each one is its own query — so growing the grid needs no
 * state of its own, and turning the page on a wide screen keeps replacing, as the canon draws
 * it. A page missing from the cache (a direct jump) simply is not there to concatenate.
 */
export function useStackedCatalogPages({
  query,
  page,
  isStacking,
}: {
  query: CatalogQuery;
  page: CatalogPage;
  isStacking: boolean;
}): readonly ListedPiece[] {
  const client = useQueryClient();

  if (!isStacking) return page.items;

  const stacked: ListedPiece[] = [];
  const seen = new Set<string>();

  for (let number = 1; number <= query.page; number += 1) {
    const cached =
      number === query.page
        ? page
        : client.getQueryData<CatalogPage>(hostCatalogQueryKey({ ...query, page: number }));

    for (const piece of cached?.items ?? []) {
      // A page that failed falls back to the one on screen, which the cache may also hold: the
      // guard is what keeps the same piece from being drawn — and keyed — twice.
      if (seen.has(piece.id)) continue;
      seen.add(piece.id);
      stacked.push(piece);
    }
  }

  return stacked;
}
